import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchStudyQueue,
  submitReview,
  undoReview,
} from '../api/study';
import { fetchCardPreview, fetchCardCurve } from '../api/cards';
import { StudyCardItem, ReviewRating, ReviewResponse, StudyQueueCounts } from '../types/study';
import { CardPreviewIntervals, CardCurve } from '../types/card';
import { formatExampleText } from '../types/word';
import { useToast } from '../context/ToastContext';
import { RetentionCurveChart } from '../components/RetentionCurveChart';
import {
  Volume2,
  RotateCcw,
  Sparkles,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Tag,
  Undo2,
} from 'lucide-react';

interface StudyPageProps {
  onNavigateToImport: () => void;
  onNavigateToWordbook: () => void;
}

export const StudyPage: React.FC<StudyPageProps> = ({
  onNavigateToImport,
  onNavigateToWordbook,
}) => {
  const { showToast } = useToast();

  const [queue, setQueue] = useState<StudyCardItem[]>([]);
  const [counts, setCounts] = useState<StudyQueueCounts>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Card interaction state
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [cardStartTime, setCardStartTime] = useState<number>(Date.now());
  const [intervals, setIntervals] = useState<CardPreviewIntervals | null>(null);
  const [curve, setCurve] = useState<CardCurve | null>(null);
  const [showCurve, setShowCurve] = useState<boolean>(false);

  // Undo stack
  const [lastReview, setLastReview] = useState<{
    reviewId: string;
    card: StudyCardItem;
  } | null>(null);
  const [undoing, setUndoing] = useState<boolean>(false);

  // Load study queue
  const loadQueue = useCallback(async (silent: boolean = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchStudyQueue(undefined, 30);
      setQueue(data.items || []);
      setCounts(data.counts || {});
      setCurrentIndex(0);
      setIsFlipped(false);
      setIntervals(null);
      setCurve(null);
      setShowCurve(false);
      setCardStartTime(Date.now());
    } catch (err: any) {
      showToast(err.message || '获取学习队列失败', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const currentCard = queue[currentIndex] || null;

  // When current card changes, reset flip and load preview intervals
  useEffect(() => {
    if (currentCard) {
      setIsFlipped(false);
      setShowCurve(false);
      setCardStartTime(Date.now());

      // Fetch preview intervals
      fetchCardPreview(currentCard.id)
        .then((data) => setIntervals(data))
        .catch(() => setIntervals(null));

      // Fetch memory curve
      fetchCardCurve(currentCard.id)
        .then((data) => setCurve(data))
        .catch(() => setCurve(null));
    } else {
      setIntervals(null);
      setCurve(null);
    }
  }, [currentCard]);

  // Audio pronunciation
  const playAudio = useCallback((text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // Format interval seconds into readable string
  const formatInterval = (sec?: number) => {
    if (sec === undefined || sec === null) return '';
    if (sec < 60) return `${Math.max(1, Math.round(sec))}秒`;
    if (sec < 3600) return `${Math.round(sec / 60)}分钟`;
    if (sec < 86400) return `${Math.round(sec / 3600)}小时`;
    const days = Math.round(sec / 86400);
    if (days < 30) return `${days}天`;
    if (days < 365) return `${(days / 30).toFixed(1)}月`;
    return `${(days / 365).toFixed(1)}年`;
  };

  // Flip card
  const handleFlip = () => {
    if (!isFlipped) {
      setIsFlipped(true);
    }
  };

  // Submit Review rating
  const handleRate = async (rating: ReviewRating) => {
    if (!currentCard || submitting) return;
    setSubmitting(true);

    const durationMs = Math.max(500, Date.now() - cardStartTime);
    const cardToReview = currentCard;

    try {
      const res: ReviewResponse = await submitReview({
        card_id: cardToReview.id,
        rating,
        duration_ms: durationMs,
        expected_version: cardToReview.version,
        idempotency_key: crypto.randomUUID(),
      });

      // Save for possible undo
      if (res.review && res.review.id) {
        setLastReview({
          reviewId: res.review.id,
          card: cardToReview,
        });
      }

      // If more cards exist in current local batch, move forward
      if (currentIndex + 1 < queue.length) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        // Local queue batch finished! Check server for remaining cards (or learning steps)
        const nextBatch = await fetchStudyQueue(undefined, 30);
        if (nextBatch.items && nextBatch.items.length > 0) {
          setQueue(nextBatch.items);
          setCounts(nextBatch.counts || {});
          setCurrentIndex(0);
          showToast('已加载下一批待学卡片', 'info');
        } else {
          setQueue([]);
          setCounts(nextBatch.counts || {});
        }
      }
    } catch (err: any) {
      showToast(err.message || '提交复习记录失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Undo last review using returned card's new version
  const handleUndo = async () => {
    if (!lastReview || undoing) return;
    setUndoing(true);
    try {
      const res = await undoReview(lastReview.reviewId);
      if (res.card) {
        // CRITICAL: Must use returned card with new version, not old card version!
        const restoredCard: StudyCardItem = {
          ...(res.card as StudyCardItem),
          word: (res.card as any).word || lastReview.card.word,
        };
        setQueue((prev) => {
          const copy = [...prev];
          copy.splice(currentIndex, 0, restoredCard);
          return copy;
        });
        showToast('已撤销上一张卡片的评分', 'info');
      } else {
        // Fallback: re-fetch queue from server
        await loadQueue(true);
        showToast('已撤销评分并同步队列', 'info');
      }
      setLastReview(null);
    } catch (err: any) {
      showToast(err.message || '撤销失败', 'error');
    } finally {
      setUndoing(false);
    }
  };

  // Keyboard navigation: Space to flip, 1-4 for ratings
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        if (!isFlipped) {
          setIsFlipped(true);
        }
      } else if (isFlipped && !submitting) {
        if (e.key === '1') {
          e.preventDefault();
          handleRate('again');
        } else if (e.key === '2') {
          e.preventDefault();
          handleRate('hard');
        } else if (e.key === '3') {
          e.preventDefault();
          handleRate('normal');
        } else if (e.key === '4') {
          e.preventDefault();
          handleRate('easy');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFlipped, submitting, currentCard]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm">正在加载今日复习队列...</p>
      </div>
    );
  }

  // Empty queue state
  if (!currentCard || queue.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner mb-6">
          <Sparkles className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">当前暂时没有到期卡片</h2>
        <p className="text-sm text-slate-600 max-w-md mx-auto mb-6 leading-relaxed">
          {counts.reviews_today && counts.reviews_today > 0
            ? `今日已累计复习 ${counts.reviews_today} 次。若刚刚有评分「Again」的生词，会在设定的初学/重学步骤（如 1 分钟或 10 分钟后）重新到期。`
            : '词库中尚无到期卡片。根据 FSRS 记忆模型，无需过度疲劳无效刷词。'}
        </p>

        <div className="mb-8">
          <button
            onClick={() => loadQueue()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-300 hover:border-emerald-500 text-slate-700 hover:text-emerald-700 font-semibold text-xs rounded-xl shadow-sm transition-all"
          >
            <RotateCcw className="w-4 h-4 text-emerald-600" />
            重新检查是否有短步到期卡片
          </button>
        </div>

        {/* Action options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-left">
          <div
            onClick={onNavigateToImport}
            className="p-5 bg-white border border-slate-200 hover:border-emerald-300 rounded-2xl shadow-sm hover:shadow transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slate-800 group-hover:text-emerald-700">导入再生词</span>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
            </div>
            <p className="text-xs text-slate-500">
              从 CSV、TXT、TSV 或 JSON 文件批量导入新词库
            </p>
          </div>

          <div
            onClick={onNavigateToWordbook}
            className="p-5 bg-white border border-slate-200 hover:border-emerald-300 rounded-2xl shadow-sm hover:shadow transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slate-800 group-hover:text-emerald-700">浏览生词本</span>
              <BookOpen className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 transition-colors" />
            </div>
            <p className="text-xs text-slate-500">
              按词本查阅单词、添加例句或单独标记熟悉度
            </p>
          </div>
        </div>

        {lastReview && (
          <div className="mt-8">
            <button
              onClick={handleUndo}
              disabled={undoing}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 underline underline-offset-4 disabled:opacity-50"
            >
              <Undo2 className="w-3.5 h-3.5" />
              刚才误操作了？撤销上一个评分
            </button>
          </div>
        )}
      </div>
    );
  }

  const word = currentCard.word || { term: '', meaning: '' };
  const isProduction = currentCard.template === 'production';

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Top Header & Queue Progress Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
            待学: {counts.available ?? (queue.length - currentIndex)}
          </span>
          {counts.new_remaining !== undefined && (
            <span className="text-xs font-medium text-blue-600 bg-white px-2 py-0.5 rounded border border-slate-200">
              新词额度: {counts.new_remaining}
            </span>
          )}
          {counts.reviews_remaining !== undefined && (
            <span className="text-xs font-medium text-emerald-600 bg-white px-2 py-0.5 rounded border border-slate-200">
              复习额度: {counts.reviews_remaining}
            </span>
          )}
          {counts.reviews_today !== undefined && (
            <span className="text-xs font-medium text-slate-500 hidden sm:inline">
              今日已学: {counts.reviews_today} 次
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {lastReview && (
            <button
              onClick={handleUndo}
              disabled={undoing}
              title="撤销上一次评分"
              className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-2.5 py-1.5 rounded-lg shadow-sm hover:bg-slate-50 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5" />
              <span>撤销</span>
            </button>
          )}

          <button
            onClick={() => loadQueue()}
            title="刷新队列"
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Flashcard Container */}
      <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-200 overflow-hidden transition-all duration-300">
        {/* Card Header Info */}
        <div className="px-6 py-3.5 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span className={`font-semibold px-2 py-0.5 rounded ${
              isProduction ? 'bg-amber-100 text-amber-900' : 'bg-slate-200/70 text-slate-700'
            }`}>
              {isProduction ? '拼写 / 产出模式' : '英汉识别模式'}
            </span>
            {word.deck_name && (
              <span className="px-2 py-0.5 bg-slate-200/70 text-slate-600 rounded">
                {word.deck_name}
              </span>
            )}
            {currentCard.is_new && (
              <span className="px-1.5 py-0.2 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">
                新词
              </span>
            )}
            {currentCard.leech && (
              <span className="px-1.5 py-0.2 bg-red-100 text-red-700 rounded text-[10px] font-semibold">
                难词 (Leech)
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {curve && (
              <button
                type="button"
                onClick={() => setShowCurve(!showCurve)}
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ${
                  showCurve
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-slate-500 hover:bg-slate-200/60'
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                <span>FSRS 遗忘曲线</span>
              </button>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className="p-8 sm:p-10">
          {/* FRONT SIDE */}
          <div className="text-center">
            {isProduction ? (
              /* Production Front: Show Chinese meaning, hide English term, phonetic and audio */
              <div className="mb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 inline-block mb-3">
                  拼写提示：请根据中文回忆对应英语单词
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-relaxed">
                  {word.meaning || formatExampleText(word.definitions?.[0]) || '中文释义待补充'}
                </h1>
                {word.parts_of_speech && word.parts_of_speech.length > 0 && (
                  <p className="text-xs font-mono text-slate-500 mt-2">
                    [{word.parts_of_speech.join(', ')}]
                  </p>
                )}
              </div>
            ) : (
              /* Recognition Front: Show English word, phonetic and audio pronunciation */
              <>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                    {word.term}
                  </h1>
                  <button
                    onClick={() => playAudio(word.term)}
                    title="发音朗读"
                    className="w-9 h-9 rounded-full bg-slate-100 hover:bg-emerald-50 text-slate-500 hover:text-emerald-700 flex items-center justify-center transition-colors shadow-sm"
                  >
                    <Volume2 className="w-5 h-5" />
                  </button>
                </div>

                {word.phonetic && (
                  <p className="text-sm font-mono text-slate-500 mb-6">
                    /{word.phonetic.replace(/^\/|\/$/g, '')}/
                  </p>
                )}
              </>
            )}

            {!isFlipped && (
              <div className="py-12 border-t border-slate-100 mt-6">
                <p className="text-sm text-slate-400 mb-6 font-medium">
                  {isProduction
                    ? '先在脑海中拼写或默背英文单词，再翻面核对答案'
                    : '先在脑海中回忆词义与用法，再翻面核对'}
                </p>
                <button
                  type="button"
                  onClick={handleFlip}
                  className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold shadow-md shadow-emerald-200 hover:shadow-lg transition-all transform active:scale-95 cursor-pointer"
                >
                  {isProduction ? '翻面核对英文拼写 (按空格键)' : '点击显示答案 (按空格键)'}
                </button>
              </div>
            )}
          </div>

          {/* BACK SIDE */}
          {isFlipped && (
            <div className="border-t border-slate-100 pt-6 mt-4 text-left animate-in fade-in duration-200">
              {isProduction && (
                /* Production Mode Answer Reveal: English term, phonetic, and audio pronunciation */
                <div className="mb-6 p-4 bg-emerald-50/70 border border-emerald-200 rounded-xl">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800 block mb-1">
                    正确英文拼写与发音
                  </span>
                  <div className="flex items-center gap-3">
                    <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                      {word.term}
                    </h2>
                    <button
                      onClick={() => playAudio(word.term)}
                      title="标准发音"
                      className="w-9 h-9 rounded-full bg-white hover:bg-emerald-100 text-slate-600 hover:text-emerald-700 flex items-center justify-center transition-colors shadow-sm"
                    >
                      <Volume2 className="w-5 h-5" />
                    </button>
                  </div>
                  {word.phonetic && (
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      /{word.phonetic.replace(/^\/|\/$/g, '')}/
                    </p>
                  )}
                </div>
              )}

              {/* Meaning */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  中文释义
                </h4>
                <p className="text-xl font-semibold text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {word.meaning || formatExampleText(word.definitions?.[0]) || '暂无释义'}
                </p>
              </div>

              {/* Examples (Safely formatted to prevent object rendering crashes) */}
              {word.examples && word.examples.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    例句与搭配
                  </h4>
                  <ul className="space-y-2">
                    {word.examples.map((ex, i) => (
                      <li
                        key={i}
                        className="text-sm text-slate-700 bg-slate-50 border border-slate-100 p-3 rounded-lg leading-relaxed italic"
                      >
                        "{formatExampleText(ex)}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {word.tags && word.tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-6">
                  <Tag className="w-3.5 h-3.5 text-slate-400 mr-1" />
                  {word.tags.map((t) => (
                    <span
                      key={t}
                      className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* FSRS Retention Curve Panel (Toggleable) */}
              {showCurve && curve && (
                <div className="mb-6">
                  <RetentionCurveChart curve={curve} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rating Buttons Footer (Visible only after flip) */}
        {isFlipped && (
          <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3 text-xs text-slate-500">
              <span>请如实评估回忆难度（快捷键 1/2/3/4）:</span>
              <span className="font-mono text-slate-400">Normal = FSRS Good</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {/* Again */}
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleRate('again')}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-red-200 bg-red-50/70 hover:bg-red-100/90 text-red-900 transition-all cursor-pointer group shadow-sm"
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono px-1 rounded bg-red-200/70 text-red-800">1</span>
                  <span className="font-bold text-sm">重来 (Again)</span>
                </div>
                <span className="text-xs text-red-700 font-mono mt-1">
                  {intervals ? formatInterval(intervals.again?.interval_seconds) : '重入步骤'}
                </span>
                <span className="text-[10px] text-red-600/80 mt-0.5">完全遗忘</span>
              </button>

              {/* Hard */}
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleRate('hard')}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-amber-200 bg-amber-50/70 hover:bg-amber-100/90 text-amber-900 transition-all cursor-pointer group shadow-sm"
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono px-1 rounded bg-amber-200/70 text-amber-800">2</span>
                  <span className="font-bold text-sm">困难 (Hard)</span>
                </div>
                <span className="text-xs text-amber-700 font-mono mt-1">
                  {intervals ? formatInterval(intervals.hard?.interval_seconds) : '小步延长'}
                </span>
                <span className="text-[10px] text-amber-600/80 mt-0.5">吃力回忆</span>
              </button>

              {/* Normal (Good) */}
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleRate('normal')}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-blue-200 bg-blue-50/70 hover:bg-blue-100/90 text-blue-900 transition-all cursor-pointer group shadow-sm ring-1 ring-blue-300"
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono px-1 rounded bg-blue-200/70 text-blue-800">3</span>
                  <span className="font-bold text-sm">良好 (Good)</span>
                </div>
                <span className="text-xs text-blue-700 font-mono mt-1">
                  {intervals ? formatInterval(intervals.normal?.interval_seconds) : '标准间隔'}
                </span>
                <span className="text-[10px] text-blue-600/80 mt-0.5">正确回忆</span>
              </button>

              {/* Easy */}
              <button
                type="button"
                disabled={submitting}
                onClick={() => handleRate('easy')}
                className="flex flex-col items-center justify-center p-3 rounded-xl border border-emerald-200 bg-emerald-50/70 hover:bg-emerald-100/90 text-emerald-900 transition-all cursor-pointer group shadow-sm"
              >
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono px-1 rounded bg-emerald-200/70 text-emerald-800">4</span>
                  <span className="font-bold text-sm">简单 (Easy)</span>
                </div>
                <span className="text-xs text-emerald-700 font-mono mt-1">
                  {intervals ? formatInterval(intervals.easy?.interval_seconds) : '大步延长'}
                </span>
                <span className="text-[10px] text-emerald-600/80 mt-0.5">秒答牢记</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer tips */}
      <div className="mt-4 text-center text-xs text-slate-400">
        💡 快捷键提示：按 <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Space</kbd> 翻转卡片，翻开后按 <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">1</kbd> / <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">2</kbd> / <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">3</kbd> / <kbd className="px-1 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">4</kbd> 快速评分
      </div>
    </div>
  );
};
